/**
 * MyTasksRow — Phase 2 MY TASKS (May 23 2026)
 *
 * Single shared component for the new strict-naming "My Tasks" list.
 * Used by FreelancerDashboardNew, VendorTasksPage, EmployeeDashboard,
 * and Vendor producer team view.
 *
 * Each row shows ONE task with:
 *   • Canonical expected filename (e.g. HNG_SH001_LAY_v001.blend)
 *   • Linked WO number + project name (allotment → WO → submission chain)
 *   • Status pill (Pending / In Progress / Submitted / Approved / Revise)
 *   • Inline green SUBMIT button → opens file-picker scoped to THIS task
 *
 * Upload rule (founder spec):
 *   "until the naming is not right it wont upload"
 *
 * The picker POSTs to /api/reviews/upload-media with task_id + enforce_canonical=true.
 * Backend rejects mismatched filenames with a 400 + diagnostic detail. The row
 * surfaces that error inline so the artist learns the canonical pattern fast.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, Check, AlertTriangle, Copy, ChevronRight, Hash, FileVideo, Film, FileBox, Info, Lightbulb, MessageCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_PILL = {
  pending:     { label: 'PENDING',     cls: 'bg-zinc-800 text-zinc-300 border-zinc-700' },
  in_progress: { label: 'IN PROGRESS', cls: 'bg-amber-500/10 text-amber-300 border-amber-500/40' },
  submitted:   { label: 'SUBMITTED',   cls: 'bg-violet-500/15 text-violet-300 border-violet-500/40' },
  in_review:   { label: 'IN REVIEW',   cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  approved:    { label: 'APPROVED',    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  changes_requested: { label: 'REVISION', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40' },
  revise:      { label: 'REVISION',    cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40' },
};

// Founder ask May 24, 2026: "make the MY TASKS rows colorful and interesting".
// Each discipline gets a left-border accent + a chip-colour so a row is
// recognisable at a glance.
const DISCIPLINE_ACCENT = {
  storyboard:  { border: 'border-l-purple-500',   chip: 'bg-purple-500/15 text-purple-300 border-purple-500/40' },
  animatics:   { border: 'border-l-fuchsia-500',  chip: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40' },
  cba:         { border: 'border-l-violet-500',   chip: 'bg-violet-500/15 text-violet-300 border-violet-500/40' },
  asset_build: { border: 'border-l-cyan-500',     chip: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' },
  rigging:     { border: 'border-l-amber-500',    chip: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  layout:      { border: 'border-l-emerald-500',  chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  animation:   { border: 'border-l-blue-500',     chip: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  lighting:    { border: 'border-l-yellow-500',   chip: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40' },
  compositing: { border: 'border-l-pink-500',     chip: 'bg-pink-500/15 text-pink-300 border-pink-500/40' },
  fx:          { border: 'border-l-orange-500',   chip: 'bg-orange-500/15 text-orange-300 border-orange-500/40' },
  sound:       { border: 'border-l-teal-500',     chip: 'bg-teal-500/15 text-teal-300 border-teal-500/40' },
};

// IN REVIEW → REVISION → APPROVED — visual lifecycle shown above the row's
// inline controls once the artist has submitted at least one version.
function StatusLifecycle({ status }) {  const steps = [
    { key: 'submitted',  label: 'SUBMITTED',  color: 'violet'  },
    { key: 'in_review',  label: 'IN REVIEW',  color: 'blue'    },
    { key: 'revise',     label: 'REVISION',   color: 'rose'    },
    { key: 'approved',   label: 'APPROVED',   color: 'emerald' },
  ];
  const norm = (status || '').toLowerCase();
  const activeIdx = (() => {
    if (norm === 'approved' || norm === 'final_approved') return 3;
    if (norm === 'revise' || norm === 'changes_requested' || norm.includes('rev')) return 2;
    if (norm === 'in_review' || norm.includes('review')) return 1;
    if (norm === 'submitted') return 0;
    return -1;
  })();
  if (activeIdx < 0) return null;
  return (
    <div className="flex items-center gap-1.5 mt-2" data-testid="status-lifecycle">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const isPast = i < activeIdx && !(activeIdx === 2 && i < 2); // revision is a side-arm, not "past"
        const tone = isActive
          ? `bg-${s.color}-500 text-zinc-950`
          : isPast
            ? `bg-${s.color}-500/30 text-${s.color}-200`
            : 'bg-zinc-800 text-zinc-600';
        return (
          <React.Fragment key={s.key}>
            <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${tone}`}>
              {s.label}
            </span>
            {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-700" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * ProducerFeedbackStrip — read-only producer (Supervisor + PM) feedback
 * for the artist's current submission on this task.
 *
 * Founder spec Feb 2026: artists do NOT enter the Approvals/review tool.
 * They read producer notes inline here. Endpoint:
 *   GET /api/reviews/producer-feedback/by-task/{task_id}
 * Returns only Supervisor + PM events (never client comments — PM relays
 * any client feedback verbally / via the relay note on the latest revise).
 *
 * UI rules:
 *   • Hidden when no submission exists for the task.
 *   • Decision pills are coloured: green=approved, amber=revise, rose=rejected.
 *   • Each pill click expands the full note text in-place.
 *   • A "+N earlier round(s)" expandable section surfaces history.
 * ────────────────────────────────────────────────────────────────────── */
function ProducerFeedbackStrip({ taskId, api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSup, setExpandedSup] = useState(false);
  const [expandedPm, setExpandedPm] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  useEffect(() => {
    if (!taskId || !api) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/reviews/producer-feedback/by-task/${encodeURIComponent(taskId)}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [taskId, api]);

  if (loading || !data || data.empty) return null;
  const { supervisor: sup, pm, history = [], round } = data;
  if (!sup && !pm && history.length === 0) return null;

  const decisionCls = (d) => {
    if (d === 'approved') return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40';
    if (d === 'rejected') return 'bg-rose-500/15 text-rose-200 border-rose-500/40';
    if (d === 'revise')   return 'bg-amber-500/15 text-amber-200 border-amber-500/40';
    return 'bg-zinc-800 text-zinc-300 border-zinc-700';
  };
  const decisionLabel = (d) => (d === 'approved' ? 'APPROVED' : d === 'rejected' ? 'REJECTED' : d === 'revise' ? 'REVISE' : (d || '').toUpperCase());

  return (
    <div className="mt-2 space-y-1.5" data-testid={`my-task-row-${taskId}-producer-feedback`}>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold flex items-center gap-1.5">
        <MessageCircle className="w-2.5 h-2.5 text-cyan-400" />
        Producer Feedback <span className="text-zinc-600 font-normal normal-case">— read-only · round {round}</span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {sup && (
          <button
            type="button"
            onClick={() => setExpandedSup(v => !v)}
            data-testid={`my-task-row-${taskId}-feedback-supervisor`}
            className={`flex flex-col items-start gap-0.5 px-2 py-1 rounded border text-left transition-all ${
              expandedSup ? 'w-full ' + decisionCls(sup.decision) : decisionCls(sup.decision)
            }`}
            title={`Supervisor ${decisionLabel(sup.decision)} by ${sup.reviewer_name} — click to expand note`}
          >
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[8px] uppercase tracking-wider px-1 rounded bg-zinc-950/40 font-bold">Supervisor</span>
              <span className="text-[8px] uppercase tracking-wider font-bold">{decisionLabel(sup.decision)}</span>
            </div>
            {expandedSup ? (
              <>
                {sup.note ? (
                  <p className="text-[10px] whitespace-pre-wrap leading-snug mt-1 max-w-full break-words">{sup.note}</p>
                ) : (
                  <p className="text-[9px] italic opacity-70 mt-1">No note attached.</p>
                )}
                <p className="text-[8px] italic opacity-60 mt-1">
                  — {sup.reviewer_name}{sup.at ? ` · ${new Date(sup.at).toLocaleDateString()}` : ''}
                </p>
              </>
            ) : (
              sup.note && (
                <p className="text-[8px] truncate max-w-[16rem] italic opacity-80">
                  &ldquo;{sup.note.slice(0, 60)}{sup.note.length > 60 ? '…' : ''}&rdquo;
                </p>
              )
            )}
          </button>
        )}
        {pm && (
          <button
            type="button"
            onClick={() => setExpandedPm(v => !v)}
            data-testid={`my-task-row-${taskId}-feedback-pm`}
            className={`flex flex-col items-start gap-0.5 px-2 py-1 rounded border text-left transition-all ${
              expandedPm ? 'w-full ' + decisionCls(pm.decision) : decisionCls(pm.decision)
            }`}
            title={`PM ${decisionLabel(pm.decision)} by ${pm.reviewer_name} — click to expand note`}
          >
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[8px] uppercase tracking-wider px-1 rounded bg-zinc-950/40 font-bold">PM</span>
              <span className="text-[8px] uppercase tracking-wider font-bold">{decisionLabel(pm.decision)}</span>
            </div>
            {expandedPm ? (
              <>
                {pm.note ? (
                  <p className="text-[10px] whitespace-pre-wrap leading-snug mt-1 max-w-full break-words">{pm.note}</p>
                ) : (
                  <p className="text-[9px] italic opacity-70 mt-1">No note attached.</p>
                )}
                <p className="text-[8px] italic opacity-60 mt-1">
                  — {pm.reviewer_name}{pm.at ? ` · ${new Date(pm.at).toLocaleDateString()}` : ''}
                </p>
              </>
            ) : (
              pm.note && (
                <p className="text-[8px] truncate max-w-[16rem] italic opacity-80">
                  &ldquo;{pm.note.slice(0, 60)}{pm.note.length > 60 ? '…' : ''}&rdquo;
                </p>
              )
            )}
          </button>
        )}
        {history.length > 0 && (
          <button
            type="button"
            onClick={() => setExpandedHistory(v => !v)}
            data-testid={`my-task-row-${taskId}-feedback-history`}
            className="flex flex-col items-start gap-0.5 px-2 py-1 rounded border text-left transition-all bg-zinc-900 border-zinc-700 hover:border-zinc-500"
          >
            <span className="text-[8px] uppercase tracking-wider font-bold text-zinc-400 flex items-center gap-1">
              Earlier rounds × {history.length}
              <ChevronRight className={`w-2.5 h-2.5 transition-transform ${expandedHistory ? 'rotate-90' : ''}`} />
            </span>
            {expandedHistory && (
              <div className="flex flex-col gap-1 mt-1 w-full">
                {history.map((h, idx) => (
                  <div key={idx} className={`px-1.5 py-1 rounded border text-[9px] ${decisionCls(h.decision)}`}>
                    <span className="font-bold uppercase tracking-wider">
                      v{h.round} · {h.phase === 'pm' ? 'PM' : 'Supervisor'} · {decisionLabel(h.decision)}
                    </span>
                    {h.note && <p className="opacity-90 whitespace-pre-wrap leading-snug mt-0.5">{h.note}</p>}
                    <p className="opacity-60 italic mt-0.5">— {h.reviewer_name}{h.at ? ` · ${new Date(h.at).toLocaleDateString()}` : ''}</p>
                  </div>
                ))}
              </div>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * RawMaterialStrip — surfaces the approved upstream files (and per-shot
 * Lighting Brief, when applicable) every artist needs to do their work.
 *
 * Founder bug Feb 2026 (production vawworld.in screenshot, freelancer
 * dashboard for Rahul Sudhakar Nimborkar): the Raw Material section is
 * INVISIBLE on My Tasks for every freelancer / vendor / employee.
 *
 * Root cause: MyTasksRow.js (this file — used by Freelancer / Vendor /
 * Employee dashboards) never rendered raw materials at all. The
 * Raw Material chip strip lives on WorkCard.js, which is used by
 * Admin/PM Allotment view only. So the data was there
 * (/api/tasks/{id}/inputs returns it) — just no UI to display it.
 *
 * Fix: hit /api/tasks/{task.id}/inputs from this row, render the
 * approved upstream files as compact chips, and render the Lighting
 * Brief (when task is lighting) as a special purple pill that expands
 * to show Day/Night/INT/EXT + the PM's notes. NAS paths are surfaced
 * under each file chip so artists can copy/paste into Blender's File
 * Browser without leaving the page.
 * ────────────────────────────────────────────────────────────────────── */
function RawMaterialStrip({ taskId, api, taskType }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedBrief, setExpandedBrief] = useState(false);

  useEffect(() => {
    if (!taskId || !api) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(`/tasks/${taskId}/inputs`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Could not load raw materials');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [taskId, api]);

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-[9px] text-zinc-600">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading raw materials…
      </div>
    );
  }
  if (error) return null;  // silent fail — surfacing errors on every row would be noisy
  if (!data) return null;

  const inputs = data.inputs || [];
  const upstreamTypes = data.upstream_types || [];

  // Split brief from file inputs early so the empty-state check can run
  // against FILE inputs only — having a brief saved doesn't mean the
  // upstream layout/animation deliverables have actually been approved.
  const briefInput = inputs.find(i => i.kind === 'brief');
  const fileInputs = inputs.filter(i => i.kind !== 'brief');

  // Empty state — tell the artist WHY they have no raw material FILES
  // yet. For animatics, upstreamTypes = ['storyboard']: "Waiting on
  // approved storyboard". For lighting with a brief but no upstream
  // approvals yet, this still renders below the brief chip so the
  // artist sees both.
  const renderEmptyHint = () => {
    if (upstreamTypes.length === 0 || fileInputs.length > 0) return null;
    const labels = upstreamTypes.map(t => t.replace(/_/g, ' '));
    return (
      <div
        className="px-2 py-1 rounded-sm bg-zinc-900/60 border border-zinc-800 text-[9px] text-amber-300 flex items-start gap-1.5"
        data-testid={`my-task-row-${taskId}-raw-material-empty`}
        title={`The approved ${labels.join(' / ')} file(s) for this task will appear here as raw materials once your PM has approved them.`}
      >
        <Info className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
        <span>Waiting on approved {labels.join(' + ')}</span>
      </div>
    );
  };

  // Nothing to show — no brief, no files, no upstream hint.
  if (inputs.length === 0 && upstreamTypes.length === 0) return null;

  return (
    <div className="mt-2 space-y-1.5" data-testid={`my-task-row-${taskId}-raw-materials`}>
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">
        Raw Materials <span className="text-zinc-600 font-normal normal-case">— click any chip to open</span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {/* Lighting brief — special expandable purple pill */}
        {briefInput && (
          <button
            type="button"
            onClick={() => setExpandedBrief(v => !v)}
            data-testid={`my-task-row-${taskId}-brief-chip`}
            className={`flex flex-col items-start gap-0.5 px-2 py-1 rounded border text-left transition-all ${
              expandedBrief
                ? 'bg-violet-500/15 border-violet-500/60 w-full'
                : 'bg-zinc-900 border-violet-500/40 hover:border-violet-500/70'
            }`}
            title="Lighting Brief — click to expand"
          >
            <div className="flex items-center gap-1 flex-wrap">
              <Lightbulb className="w-2.5 h-2.5 text-violet-400" />
              <span className="text-[9px] uppercase tracking-wider font-bold text-violet-200">Brief</span>
              {briefInput.time_of_day && (
                <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-500/40 font-bold">
                  {briefInput.time_of_day}
                </span>
              )}
              {briefInput.bg_type && (
                <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-200 border border-cyan-500/40 font-bold">
                  {briefInput.bg_type === 'interior' ? 'INT' : briefInput.bg_type === 'exterior' ? 'EXT' : briefInput.bg_type}
                </span>
              )}
            </div>
            {expandedBrief && briefInput.brief && (
              <p className="text-[10px] text-zinc-300 whitespace-pre-wrap leading-snug mt-1 max-w-full break-words">
                {briefInput.brief}
              </p>
            )}
            {!expandedBrief && briefInput.brief && (
              <p className="text-[8px] text-zinc-500 truncate max-w-[14rem] italic">
                &ldquo;{briefInput.brief.slice(0, 50)}{briefInput.brief.length > 50 ? '…' : ''}&rdquo;
              </p>
            )}
          </button>
        )}

        {/* File chips — one per approved upstream file.
            Founder bug Feb 2026 (vawworld.in, Rahul's animatics for
            Thumbelina episode): RAW_MATERIAL_DEPENDENCIES['animatics']
            = ['script', 'concept_art'], and a typical episode has
            15-20 concept arts (every character + prop + environment).
            The artist's MY TASKS row was showing all 19 chips in a
            single row — overwhelming and "clumsy" per founder
            feedback. Fix: group chips by stage; when a single stage
            has more than 4 files, collapse to ONE expandable
            "Concept Art × 19" pill. Click expands the full list. */}
        {(() => {
          // Group file inputs by canonical stage. Preserves the order
          // of first appearance so the most-relevant upstream (per
          // RAW_MATERIAL_DEPENDENCIES) stays at the top.
          const byStage = new Map();
          fileInputs.forEach((inp, idx) => {
            const stage = (inp.stage || 'other').toLowerCase();
            if (!byStage.has(stage)) byStage.set(stage, []);
            byStage.get(stage).push({ ...inp, _origIdx: idx });
          });
          const elements = [];
          byStage.forEach((arr, stage) => {
            if (arr.length > 4) {
              elements.push(
                <CollapsedStageChip key={`group-${stage}`}
                  stage={stage} files={arr} taskId={taskId} />
              );
            } else {
              arr.forEach((inp) => {
                elements.push(
                  <FileChip key={`f-${inp._origIdx}`}
                    inp={inp} idx={inp._origIdx} taskId={taskId} />
                );
              });
            }
          });
          return elements;
        })()}
      </div>
      {/* When no FILE inputs are approved yet, surface the
          "Waiting on approved <X>" hint — even if a brief is saved
          alongside (the brief is informational, the upstream files
          are what actually unblock work). */}
      {renderEmptyHint()}
    </div>
  );
}

/* ───────────── File chip helpers (extracted Feb 2026) ───────────── */

function FileChip({ inp, idx, taskId }) {
  const stage = (inp.stage || '').toLowerCase();
  const accent = DISCIPLINE_ACCENT[stage] || { chip: 'bg-zinc-800 text-zinc-300 border-zinc-700' };
  return (
    <a
      href={inp.url || '#'}
      target={inp.url ? '_blank' : undefined}
      rel="noreferrer"
      data-testid={`my-task-row-${taskId}-input-${idx}`}
      onClick={(e) => { if (!inp.url) e.preventDefault(); }}
      className={`flex items-start gap-1.5 px-2 py-1 rounded border text-[9px] hover:scale-[1.02] transition-all ${accent.chip}`}
      title={inp.filename ? `Open ${inp.filename}` : 'Open'}
    >
      <FileBox className="w-2.5 h-2.5 mt-0.5 flex-shrink-0 opacity-80" />
      <span className="flex flex-col min-w-0 max-w-[12rem]">
        <span className="font-bold uppercase tracking-wider truncate">{inp.label}</span>
        {inp.filename && (
          <span className="text-[8px] text-zinc-400 font-mono truncate" title={inp.filename}>{inp.filename}</span>
        )}
        {inp.nas_rel_path && (
          <span
            className="text-[8px] text-cyan-400 font-mono truncate mt-0.5"
            title={`NAS: ${inp.nas_rel_path} — click to copy path for Blender / Maya`}
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              if (navigator.clipboard) {
                navigator.clipboard.writeText(inp.nas_rel_path).catch(() => {});
              }
            }}
          >
            ⎘ {inp.nas_rel_path}
          </span>
        )}
      </span>
    </a>
  );
}

/* CollapsedStageChip — when an upstream stage (e.g. concept_art) has
   >4 approved files, collapse them into one expandable pill so the
   row doesn't become a 20-chip eyesore. Founder spec Feb 2026. */
function CollapsedStageChip({ stage, files, taskId }) {
  const [expanded, setExpanded] = useState(false);
  const accent = DISCIPLINE_ACCENT[stage] || { chip: 'bg-zinc-800 text-zinc-300 border-zinc-700' };
  const label = (stage || 'other').replace(/_/g, ' ');
  return (
    <div className={`flex flex-col gap-1 ${expanded ? 'w-full' : ''}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        data-testid={`my-task-row-${taskId}-group-${stage}`}
        className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[9px] uppercase tracking-wider font-bold hover:scale-[1.02] transition-all ${accent.chip}`}
        title={`${files.length} approved ${label} files — click to ${expanded ? 'collapse' : 'expand'}`}
      >
        <FileBox className="w-2.5 h-2.5 opacity-80" />
        <span>{label}</span>
        <span className="text-[8px] bg-zinc-950/40 px-1 py-0.5 rounded">× {files.length}</span>
        <ChevronRight className={`w-2.5 h-2.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="flex flex-wrap gap-1 pl-2 border-l border-zinc-800">
          {files.map((inp) => (
            <FileChip key={inp._origIdx} inp={inp} idx={inp._origIdx} taskId={taskId} />
          ))}
        </div>
      )}
    </div>
  );
}


export default function MyTasksRow({ task, onSubmitted, dense = false }) {
  const { api } = useAuth();
  const fileRef = useRef(null);
  // Founder spec May 24, 2026: SUBMIT becomes two upload modes —
  // "Upload CBA Clip" (a playblast / preview video for review) and
  // "Upload Shot" (the working scene/source file). They both go through
  // the same canonical + DAG + FPS/resolution gate; the only difference
  // is the file picker's `accept` list and how the upload is tagged.
  const cbaRef = useRef(null);
  const shotRef = useRef(null);
  const [uploadMode, setUploadMode] = useState(null);  // 'cba' | 'shot'
  const [canonical, setCanonical] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);
  const [copied, setCopied] = useState(false);
  // Auto-fetch flow (Phase 2, Feb 2026): inline state for the
  // status dropdown. We pessimistically reflect the task's current
  // status so the UI shows "WIP" immediately on render and only
  // flips to "Submitting…" while the PUT is in flight.
  const [submittingStatus, setSubmittingStatus] = useState(false);
  const handleAutoFetchSubmit = async () => {
    if (submittingStatus) return;
    setSubmittingStatus(true);
    try {
      // PUT /api/tasks/{id} with status="submitted" — backend
      // dispatcher in server.py update_task() will fire the
      // NAS scanner as fire-and-forget. The auto_fetch_status
      // chip on this row picks up the result on next refresh.
      await api.put(`/tasks/${task.id}`, { status: 'submitted' });
      onSubmitted && onSubmitted();
    } catch (e) {
      setErr({
        kind: 'auto_fetch_dispatch',
        message: e.response?.data?.detail
                 || 'Could not submit for approval — please retry.',
      });
    } finally {
      setSubmittingStatus(false);
    }
  };
  const handleSetWip = async () => {
    if (submittingStatus) return;
    setSubmittingStatus(true);
    try {
      await api.put(`/tasks/${task.id}`, { status: 'in_progress' });
      onSubmitted && onSubmitted();
    } catch (e) {
      // No-op — silent failure is fine on a status revert
    } finally {
      setSubmittingStatus(false);
    }
  };

  // Founder spec May 24, 2026 — per-discipline upload UX:
  //   • Animatics / Storyboard / Edit / Sound / Final → SINGLE upload
  //     button (one mp4 only, max 100MB). They don't produce a CBA clip
  //     and a Shot file split — they ARE the master deliverable.
  //   • Animator (ANM) → dual CBA Clip + Shot file with LAY/ANM dropdown.
  //   • Asset build, Rigging, Lighting, Comping, Modeling, Texture,
  //     UV, Hair, Cloth, FX → dual CBA Clip + Shot file (most artists
  //     iterate on a playblast + source pair).
  const taskTypeRaw = (task.task_type || task.stage || '').toString().toLowerCase();
  // ── Auto-fetch from NAS (Phase 2 of "My Approvals", Feb 2026) ──
  // For these task types the artist no longer uploads files manually —
  // they drop the mp4 to NAS at the conventional folder, set the task
  // status to "Submit for Approval", and the backend scanner does the
  // rest. Keep this set in lock-step with TASK_TYPE_NAS_FOLDER in
  // backend/reviews_auto_fetch.py — adding a key here without the
  // matching backend mapping will surface as a graceful "unsupported"
  // error from the scanner.
  const AUTO_FETCH_TASK_TYPES = new Set([
    'layout', 'animation', 'lighting',
    'comp', 'compositing', 'render',
  ]);
  const useAutoFetchFlow = AUTO_FETCH_TASK_TYPES.has(taskTypeRaw);
  const SINGLE_FILE_DISCIPLINES = new Set([
    'animatics', 'animatic', 'storyboard', 'storyboarding',
    'edit', 'editing', 'sound', 'audio', 'sfx', 'final',
  ]);
  // Per-discipline accept patterns + max size hints (used to constrain
  // the <input type="file" accept="..."> attribute and reject early).
  const DISCIPLINE_ACCEPT = {
    animatics:   { accept: '.mp4,.mov',                 maxMb: 200, label: 'Upload Animatic (MP4)' },
    storyboard:  { accept: '.pdf,.png,.jpg,.jpeg,.zip', maxMb: 200, label: 'Upload Storyboard'      },
    edit:        { accept: '.mp4,.mov,.webm,.mkv',      maxMb: 500, label: 'Upload Edit (MP4)'      },
    sound:       { accept: '.wav,.mp3,.aiff,.flac,.zip',maxMb: 200, label: 'Upload Sound'           },
    final:       { accept: '.mp4,.mov,.mkv',            maxMb: 500, label: 'Upload Final'           },
    asset_build: { accept: '.blend,.fbx,.obj,.usd,.usda,.usdc,.glb,.zip', maxMb: 500, label: 'Upload Asset' },
    modeling:    { accept: '.blend,.fbx,.obj,.usd,.zip', maxMb: 500, label: 'Upload Model' },
    rigging:     { accept: '.blend,.ma,.mb',            maxMb: 500, label: 'Upload Rig (Blend)' },
    texture:     { accept: '.png,.jpg,.jpeg,.exr,.tif,.zip', maxMb: 500, label: 'Upload Textures' },
    lighting:    { accept: '.png,.jpg,.jpeg,.exr,.tif,.zip', maxMb: 500, label: 'Upload Lighting' },
    compositing: { accept: '.png,.jpg,.jpeg,.exr,.tif,.mp4,.mov,.zip', maxMb: 500, label: 'Upload Comp' },
    fx:          { accept: '.blend,.hip,.hipnc,.usd,.zip', maxMb: 500, label: 'Upload FX' },
  };
  const isSingleFile = SINGLE_FILE_DISCIPLINES.has(taskTypeRaw);
  const accepts = DISCIPLINE_ACCEPT[taskTypeRaw] || { accept: '.mp4,.mov,.blend,.zip', maxMb: 500, label: 'Upload' };

  // Animator stage dropdown (founder spec May 24, 2026). For a task whose
  // intrinsic discipline is ANM, the artist picks which pass they're
  // submitting (LAY or ANM). The selection drives:
  //   • the canonical filename hint shown on the row
  //   • the `stage_override` form-field on /reviews/upload-media
  //   • the recorded submission's `stage` field (so LAY → ANM DAG checks
  //     find the approval later)
  // ANM is locked until the shot has a final-approved LAY submission.
  const intrinsicDiscipline = (
    canonical?.task_code
    || (task.task_type || task.stage || '').toString().toLowerCase()
  ).toUpperCase();
  const intrinsicCode = ({
    ANIMATION: 'ANM', ANIM: 'ANM', LAYOUT: 'LAY',
  }[intrinsicDiscipline] || intrinsicDiscipline).slice(0, 3);
  const showStageDropdown = intrinsicCode === 'ANM' && !SINGLE_FILE_DISCIPLINES.has(taskTypeRaw);
  const [stagePick, setStagePick] = useState('LAY');
  const [upstream, setUpstream] = useState(null);

  // Default the dropdown: if LAY is approved, default to ANM. Else LAY.
  useEffect(() => {
    if (showStageDropdown && upstream) {
      setStagePick(upstream.can_submit ? 'ANM' : 'LAY');
    }
  }, [showStageDropdown, upstream]);

  // Fetch canonical filename for THIS task. The canonical endpoint
  // computes "project_code _ entity _ task_code _ v###" using the project's
  // source-of-truth code + the task's task_code + next version number.
  useEffect(() => {
    let cancelled = false;
    setCanonical(null); setErr(null);
    api.get(`/nomenclature/canonical-for-task/${task.id}`)
      .then(r => { if (!cancelled) setCanonical(r.data); })
      .catch(e => {
        if (cancelled) return;
        // 409 means project has no code yet — show inline so admin can fix
        setCanonical({ error: e?.response?.data?.detail || 'Unable to compute canonical name' });
      });
    return () => { cancelled = true; };
  }, [api, task.id]);

  // Animator-only: poll the upstream status for THIS shot so the ANM
  // dropdown option can be disabled until LAY is approved. Fires only
  // when we know we need it (avoids extra calls on every row).
  useEffect(() => {
    if (!showStageDropdown) { setUpstream(null); return; }
    let cancel = false;
    const parent = task.shot_code || task.entity_code || task.parent_code || '';
    if (!parent) return;
    api.get('/reviews/upstream-status', { params: {
      project_id: task.project_id,
      parent_code: parent,
      task_code: 'ANM',
    }}).then(r => { if (!cancel) setUpstream(r.data); })
      .catch(() => { if (!cancel) setUpstream({ can_submit: false, missing_upstream: ['LAY'] }); });
    return () => { cancel = true; };
  }, [api, showStageDropdown, task.project_id, task.shot_code, task.entity_code, task.parent_code]);

  const copyName = (name) => {
    navigator.clipboard?.writeText(name);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const onPickCba = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (cbaRef.current) cbaRef.current.value = '';
    if (f.size > accepts.maxMb * 1024 * 1024) {
      setErr({ kind: 'size', message: `File is ${(f.size/1048576).toFixed(1)} MB — max ${accepts.maxMb} MB for ${taskTypeRaw} uploads.`, help_tip: `Re-export at a smaller bitrate or shorter duration so it fits under ${accepts.maxMb} MB.` });
      return;
    }
    doUpload(f, 'cba');
  };
  const onPickShot = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (shotRef.current) shotRef.current.value = '';
    if (f.size > accepts.maxMb * 1024 * 1024) {
      setErr({ kind: 'size', message: `File is ${(f.size/1048576).toFixed(1)} MB — max ${accepts.maxMb} MB for ${taskTypeRaw} uploads.`, help_tip: `Re-export or archive (zip) so it fits under ${accepts.maxMb} MB.` });
      return;
    }
    doUpload(f, 'shot');
  };
  // Single-file picker (animatics / storyboard / edit / sound / final)
  const onPickSingle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (fileRef.current) fileRef.current.value = '';
    if (f.size > accepts.maxMb * 1024 * 1024) {
      setErr({ kind: 'size', message: `File is ${(f.size/1048576).toFixed(1)} MB — max ${accepts.maxMb} MB for ${taskTypeRaw} uploads.`, help_tip: `Re-export at a smaller bitrate or shorter duration so it fits under ${accepts.maxMb} MB.` });
      return;
    }
    doUpload(f, 'single');
  };

  const doUpload = async (file, mode) => {
    setBusy(true); setErr(null); setOk(false); setUploadMode(mode);
    try {
      const fd = new FormData();
      fd.append('project_id', task.project_id);
      fd.append('file', file);
      fd.append('task_id', task.id);
      fd.append('enforce_canonical', 'true');
      // Stage hint helps the backend route into the correct NAS subfolder.
      const stage = (task.stage || task.task_type || '').toLowerCase();
      fd.append('stage', stage);
      // Founder spec May 24, 2026: tag whether this is a CBA reference
      // playblast or the actual deliverable shot file. Backend can
      // route them to different NAS subfolders and surface them
      // distinctly in the Approvals UI.
      fd.append('clip_kind', mode === 'cba' ? 'cba_clip' : mode === 'single' ? 'single' : 'shot');
      // Animator dropdown: send the chosen sub-stage so backend swaps the
      // effective task_code to LAY or ANM for the canonical/DAG checks
      // AND records the submission under that discipline.
      if (showStageDropdown) {
        fd.append('stage_override', stagePick);
      }
      // For shot tasks parent_code carries the shot for dual-save routing
      const parentCode = task.shot_code || task.entity_code || '';
      fd.append('parent_code', parentCode);
      const isVid = (file.type || '').startsWith('video/')
        || /\.(mp4|mov|webm|m4v|mkv)$/i.test(file.name);
      fd.append('preset', isVid ? 'web-720' : 'none');
      const up = await api.post('/reviews/upload-media', fd);

      // Chain a /reviews/submit call so the upload becomes a tracked
      // review_submissions row. Without this step, supervisors never
      // see the work on the Approvals page AND the LAY→ANM DAG gate
      // never finds an approved LAY (because the bytes alone don't
      // count — only an approved submission counts). Founder spec
      // May 24, 2026.
      const m = up.data || {};
      // Effective stage = override if dropdown is shown, else task's discipline.
      const effectiveStage = showStageDropdown
        ? (stagePick === 'LAY' ? 'layout' : 'animation')
        : stage;
      const kind = parentCode && /^SH\d+/i.test(parentCode) ? 'shot' : 'asset';
      const submitBody = {
        project_id: task.project_id,
        kind,
        stage: effectiveStage,
        item_refs: [task.id],
        parent_code: parentCode,
        title: file.name,
        description: `Submitted via MY TASKS · ${effectiveStage.toUpperCase()}`,
        media: [{
          storage_key: m.storage_key,
          url: m.url,
          type: m.type || (isVid ? 'video' : 'file'),
          filename: m.filename || file.name,
          size: m.size || file.size,
          thumbnail_url: m.thumbnail_url || '',
          duration_sec: m.transcode?.duration_sec || null,
        }],
      };
      try {
        await api.post('/reviews/submit', submitBody);
      } catch (subErr) {
        // If /submit fails (e.g. duplicate active submission), the
        // upload-media bytes are already saved. Surface the message but
        // don't flip the row to error — the artist can resubmit a
        // revised version through the same flow without re-uploading.
        const dd = subErr?.response?.data?.detail;
        if (typeof dd === 'string' && dd.startsWith('Duplicate submission')) {
          // Already has an active submission for this item — that's fine,
          // the bytes just landed as a revision attached to the existing.
        } else {
          throw subErr;
        }
      }

      setOk(true);
      onSubmitted?.(task.id);
      setTimeout(() => setOk(false), 4000);
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Filename-rejection shape: {error, message, expected_example, help_tip, ...}
      if (d && typeof d === 'object' && d.error?.startsWith('filename_')) {
        setErr({
          kind: d.error,
          message: d.message,
          expected_example: d.expected_example,
          filename: d.filename,
          help_tip: d.help_tip,
        });
      } else if (d && typeof d === 'object' && d.error === 'dag_blocked') {
        setErr({
          kind: 'dag_blocked',
          message: d.message,
          missing_upstream: d.missing_upstream,
          missing_labels: d.missing_labels,
          help_tip: d.help_tip,
        });
      } else if (d && typeof d === 'object' && d.error === 'video_spec_mismatch') {
        setErr({
          kind: 'video_spec_mismatch',
          message: d.message,
          issues: d.issues,
          file_fps: d.file_fps,
          file_resolution: d.file_resolution,
          project_fps: d.project_fps,
          project_resolution: d.project_resolution,
          help_tip: d.help_tip,
        });
      } else {
        setErr({ kind: 'generic', message: (typeof d === 'string' ? d : d?.message) || e?.message || 'Upload failed' });
      }
    } finally {
      setBusy(false);
      setUploadMode(null);
    }
  };

  const statusKey = (task.status || 'pending').toLowerCase();
  const pill = STATUS_PILL[statusKey] || STATUS_PILL.pending;
  // Default expected filename comes from the canonical endpoint. When the
  // animator picks LAY in the dropdown, swap the discipline code so the
  // displayed hint matches what the artist must rename to. We do this
  // client-side (just replace the 3-letter discipline segment) so we
  // don't need a roundtrip per dropdown change.
  const expectedFromApi = canonical?.expected_filename || canonical?.save_current || '';
  const expectedName = (showStageDropdown && expectedFromApi)
    ? expectedFromApi.replace(/_(ANM|LAY)_/, `_${stagePick}_`)
    : expectedFromApi;
  const expectedNoCanonical = canonical?.error;
  // Animator's ANM option locked until LAY is approved for this shot.
  const animLocked = showStageDropdown && upstream && !upstream.can_submit && stagePick === 'ANM';

  const taskTypeKey = (task.task_type || task.stage || '').toLowerCase();
  const disciplineForRow = showStageDropdown
    ? (stagePick === 'LAY' ? 'layout' : 'animation')
    : taskTypeKey;
  const accent = DISCIPLINE_ACCENT[disciplineForRow]
    || DISCIPLINE_ACCENT[taskTypeKey]
    || { border: 'border-l-zinc-700', chip: 'bg-zinc-800 text-zinc-300 border-zinc-700' };

  return (
    <div
      data-testid={`my-task-row-${task.id}`}
      className={`bg-gradient-to-br from-zinc-950 via-zinc-950 to-zinc-900/60 border border-zinc-800 border-l-4 ${accent.border} rounded-md ${dense ? 'p-3' : 'p-4'} ${err ? 'border-rose-500/40' : ok ? 'border-emerald-500/40' : ''} transition-all hover:from-zinc-900 hover:border-zinc-700 hover:shadow-lg hover:shadow-zinc-900/40`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        {/* LEFT — task identity */}
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${pill.cls}`} data-testid={`my-task-row-${task.id}-status`}>
              {pill.label}
            </span>
            {/* Discipline chip — coloured per discipline */}
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${accent.chip} font-semibold`}>
              {disciplineForRow}
            </span>
            {task.work_order_no && (
              <span className="text-[10px] text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded px-1.5 py-0.5 font-mono flex items-center gap-1">
                <Hash className="w-2.5 h-2.5" />{task.work_order_no}
              </span>
            )}
            {task.project_name && (
              <span className="text-[10px] text-zinc-500 truncate max-w-[160px]" title={task.project_name}>
                {task.project_name}
              </span>
            )}
          </div>
          {/* Canonical filename — the SOURCE OF TRUTH for what the artist must save as */}
          {expectedName ? (
            <button
              onClick={() => copyName(expectedName)}
              data-testid={`my-task-row-${task.id}-canonical`}
              title="Click to copy"
              className="group flex items-center gap-1.5 text-left"
            >
              <code className="font-mono text-[13px] text-white font-semibold tracking-tight">{expectedName}</code>
              <Copy className={`w-3 h-3 ${copied ? 'text-emerald-400' : 'text-zinc-500 group-hover:text-zinc-300'}`} />
              {copied && <span className="text-[9px] text-emerald-400 uppercase tracking-wider">copied</span>}
            </button>
          ) : expectedNoCanonical ? (
            <p className="text-[11px] text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {typeof expectedNoCanonical === 'string' ? expectedNoCanonical : 'Canonical name unavailable'}
            </p>
          ) : (
            <p className="text-[11px] text-zinc-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Computing canonical name…
            </p>
          )}
          {/* Sub-line: task type + assigned artist (for vendor producer view) */}
          <p className="text-[10px] text-zinc-500">
            {task.task_type || task.stage || 'task'}
            {task.vendor_artist_name && ` · assigned to ${task.vendor_artist_name}`}
            {task.due_date && ` · due ${new Date(task.due_date).toLocaleDateString()}`}
          </p>
          {/* Status lifecycle — SUBMITTED > IN REVIEW > REVISION > APPROVED */}
          <StatusLifecycle status={task.status} />
          {/* Auto-fetch from NAS status (Phase 2 of "My Approvals", Feb 2026)
              — when artist sets task to "submitted", backend scans NAS for
              the latest playblast and creates the submission row. This
              chip surfaces the progress / failure so the artist isn't
              left wondering whether anything happened. */}
          {task.auto_fetch_status === 'pending' && (
            <div
              className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border bg-sky-500/15 text-sky-300 border-sky-500/40"
              data-testid={`my-task-row-${task.id}-auto-fetch-pending`}
            >
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              Auto-fetching from NAS…
            </div>
          )}
          {task.auto_fetch_status === 'success' && (
            <div
              className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
              data-testid={`my-task-row-${task.id}-auto-fetch-success`}
              title="Submission auto-created from NAS"
            >
              <Check className="w-2.5 h-2.5" />
              NAS fetched — submitted
            </div>
          )}
          {task.auto_fetch_status === 'failed' && (
            (() => {
              const errText = (task.auto_fetch_error || 'unknown error');
              const isNomenclature = /nomenclature|canonical|shot code|separator|underscore|SH<|rename|expected/i.test(errText);
              const isBridgeOffline = /bridge_offline|bridge offline|nas.*not.*accessible|folder not accessible/i.test(errText);
              const tint = isNomenclature
                ? 'bg-amber-500/10 border-amber-500/50'
                : isBridgeOffline
                  ? 'bg-sky-500/10 border-sky-500/50'
                  : 'bg-rose-500/10 border-rose-500/50';
              const textTint = isNomenclature
                ? 'text-amber-200'
                : isBridgeOffline
                  ? 'text-sky-200'
                  : 'text-rose-200';
              const heading = isNomenclature
                ? 'File on NAS is misnamed — auto-fetch blocked'
                : isBridgeOffline
                  ? 'NAS bridge is offline — auto-fetch on hold'
                  : 'Auto-fetch failed';
              return (
                <div
                  className={`mt-1 rounded border max-w-2xl px-2.5 py-2 ${tint}`}
                  data-testid={`my-task-row-${task.id}-auto-fetch-failed`}
                  title={errText}
                >
                  <div className={`flex items-start gap-1.5 text-[11px] font-bold uppercase tracking-wider ${textTint}`}>
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>{heading}</span>
                  </div>
                  <p className={`mt-1 text-[10.5px] leading-snug break-words ${textTint}`}>
                    {errText}
                  </p>
                  {isNomenclature && (
                    <p className="mt-1.5 text-[10px] text-amber-300/80 italic leading-snug">
                      Tip: rename the file on NAS to the canonical name shown
                      above (e.g. <code className="font-mono">PROJ_SH001_TASK_v001.mp4</code> — no
                      underscore between <code className="font-mono">SH</code> and the digits),
                      then flip the dropdown back to <b>REVIEW</b> to re-trigger.
                    </p>
                  )}
                  {isBridgeOffline && (
                    <div className="mt-1.5 text-[10px] text-sky-300/80 leading-snug space-y-1">
                      <p className="italic">
                        Tip: the QNAP bridge daemon isn&apos;t reachable from the cloud right now.
                      </p>
                      <ol className="list-decimal pl-4 space-y-0.5">
                        <li>SSH into the QNAP and check <code className="font-mono">vos_bridge_daemon</code> is running.</li>
                        <li>Restart it: <code className="font-mono">/share/CACHEDEV1_DATA/.qpkg/.../vos_bridge_daemon.sh restart</code></li>
                        <li>Once the daemon reconnects (status pill turns green at the top of Approvals Inbox), flip this task back to <b>REVIEW</b>.</li>
                      </ol>
                    </div>
                  )}
                </div>
              );
            })()
          )}
          {/* Revision-requested red badge (Phase 3, Feb 2026):
              shown when the Layout / Animation Supervisor returned a
              score below threshold. The artist sees the bullet notes
              in My Approvals → fixes them → flips REVIEW dropdown
              again to trigger a fresh round. */}
          {(task.auto_fetch_status === 'revision_requested'
             || task.status === 'revision_requested') && (
            <div
              className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border bg-rose-500/15 text-rose-300 border-rose-500/40 max-w-lg"
              data-testid={`my-task-row-${task.id}-revision-requested`}
              title={task.auto_fetch_error || 'Revisions requested by Supervisor'}
            >
              <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">
                Revisions requested — see My Approvals → fix notes → flip to REVIEW again
              </span>
            </div>
          )}
          {/* Producer Feedback — read-only Supervisor + PM notes for the
              artist's current submission. Founder spec Feb 2026: artists
              no longer enter /reviews; they read producer notes inline
              here. Hidden when there's no submission yet. */}
          <ProducerFeedbackStrip taskId={task.id} api={api} />
          {/* Raw Materials — approved upstream files (e.g. storyboard for
              animatics, layout+animation for lighting) + Lighting Brief
              chip for lighting tasks. */}
          <RawMaterialStrip taskId={task.id} api={api} taskType={taskTypeRaw} />
        </div>

        {/* RIGHT — stage dropdown (animators only) + dual upload buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {showStageDropdown && (
            <div className="flex flex-col items-end gap-0.5" data-testid={`my-task-row-${task.id}-stage-wrapper`}>
              <select
                value={stagePick}
                onChange={e => setStagePick(e.target.value)}
                disabled={busy}
                data-testid={`my-task-row-${task.id}-stage-picker`}
                className="h-9 px-2 rounded-sm bg-zinc-900 border border-zinc-700 text-[11px] font-semibold text-white uppercase tracking-wider focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                title="Pick which pass you're submitting for this shot"
              >
                <option value="LAY">LAYOUT (LAY)</option>
                <option value="ANM" disabled={upstream && !upstream.can_submit}>
                  {upstream && !upstream.can_submit ? 'ANIMATION (locked)' : 'ANIMATION (ANM)'}
                </option>
              </select>
              {upstream && stagePick === 'ANM' && !upstream.can_submit && (
                <span className="text-[9px] text-amber-300 flex items-center gap-1" title="Approved Layout required before Animation can be submitted">
                  <AlertTriangle className="w-2.5 h-2.5" /> Layout must be approved first
                </span>
              )}
              {upstream && stagePick === 'ANM' && upstream.can_submit && (
                <span className="text-[9px] text-emerald-400 flex items-center gap-1">
                  <Check className="w-2.5 h-2.5" /> Layout approved — Animation unlocked
                </span>
              )}
            </div>
          )}
          {/* Founder spec May 24, 2026: animatics/storyboard/edit/sound/final
              artists upload a SINGLE file (max 100MB). Animators use dual
              CBA-clip + Shot. Everyone else (asset/rig/lgt/cmp/fx) gets dual.
              The accept attribute is per-discipline so the OS file dialog
              already filters out wrong file types. */}
          {/* Auto-fetch flow (Phase 2, Feb 2026) — for task types where
              the artist drops the playblast to NAS and the backend
              scanner creates the submission automatically, we REPLACE
              the dual upload buttons with a simple status dropdown.
              Animator workflow becomes:
                1. Drop mp4 to NAS at <project>/03_Production/.../<shot>/
                2. Set this dropdown → "Submit for Approval"
                3. Backend fires NAS scan → submission row → supervisor
                   notification → AI Layout Supervisor review (Phase 3)
                4. Artist sees feedback in My Approvals within minutes */}
          {useAutoFetchFlow ? (
            <div className="flex items-center gap-2"
                 data-testid={`my-task-row-${task.id}-auto-fetch-dropdown`}>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                Status:
              </span>
              <select
                value={
                  task.status === 'in_review' ? 'in_review'
                    : task.status === 'submitted' ? 'submitted'
                    : task.status === 'approved' ? 'approved'
                    : task.status === 'changes_requested' || task.status === 'revise' || task.status === 'revision_requested' ? 'revise'
                    : 'in_progress'
                }
                disabled={submittingStatus
                            || task.auto_fetch_status === 'pending'
                            || task.status === 'approved'}
                data-testid={`my-task-row-${task.id}-status-select`}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'submitted') handleAutoFetchSubmit();
                  else if (v === 'in_progress') handleSetWip();
                }}
                className="h-9 px-2 bg-zinc-900 border border-zinc-700 text-zinc-100 text-[11px] font-bold rounded-sm hover:bg-zinc-800 disabled:opacity-40 cursor-pointer"
              >
                <option value="in_progress">WIP (In Progress)</option>
                <option value="submitted">REVIEW (Fetch from NAS)</option>
                {task.status === 'in_review' && (
                  <option value="in_review" disabled>In Review (locked)</option>
                )}
                {(task.status === 'changes_requested'
                   || task.status === 'revise'
                   || task.status === 'revision_requested') && (
                  <option value="revise" disabled>Revisions Requested</option>
                )}
                {task.status === 'approved' && (
                  <option value="approved" disabled>✓ Approved</option>
                )}
              </select>
              {submittingStatus && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
              )}
              {/* Inline help — tells the artist exactly where to drop
                  the file BEFORE they click submit */}
              <span
                className="text-[10px] text-zinc-500 hidden lg:inline"
                title={`Drop your ${(taskTypeRaw || 'shot')} mp4 at: <project>/03_Production/.../<shot>/`}
              >
                Drop mp4 to NAS first, then submit.
              </span>
            </div>
          ) : isSingleFile ? (
            <>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={onPickSingle}
                data-testid={`my-task-row-${task.id}-single-file-input`}
                accept={accepts.accept}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy || !!expectedNoCanonical}
                data-testid={`my-task-row-${task.id}-single-btn`}
                className="h-9 px-3 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                title={`Single file upload · max ${accepts.maxMb} MB · ${accepts.accept}`}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : ok ? <Check className="w-3.5 h-3.5" />
                  : <FileVideo className="w-3.5 h-3.5" />}
                {busy ? 'Uploading…' : ok ? 'Submitted' : accepts.label}
              </button>
            </>
          ) : (
            <>
              <input
                ref={cbaRef}
                type="file"
                className="hidden"
                onChange={onPickCba}
                data-testid={`my-task-row-${task.id}-cba-file-input`}
                accept=".mp4,.mov,.webm,.mkv,.m4v"
              />
              <input
                ref={shotRef}
                type="file"
                className="hidden"
                onChange={onPickShot}
                data-testid={`my-task-row-${task.id}-shot-file-input`}
                accept={accepts.accept}
              />
              <button
                onClick={() => cbaRef.current?.click()}
                disabled={busy || !!expectedNoCanonical || animLocked}
                data-testid={`my-task-row-${task.id}-cba-btn`}
                className="h-9 px-3 rounded-sm bg-violet-500 hover:bg-violet-400 text-zinc-950 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Upload a playblast / preview clip (.mp4 / .mov)"
              >
                {busy && uploadMode === 'cba' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : ok && uploadMode === 'cba' ? <Check className="w-3.5 h-3.5" />
                  : <Film className="w-3.5 h-3.5" />}
                {busy && uploadMode === 'cba' ? 'Uploading…' : ok && uploadMode === 'cba' ? 'Sent' : 'Upload CBA Clip'}
              </button>
              <button
                onClick={() => shotRef.current?.click()}
                disabled={busy || !!expectedNoCanonical || animLocked}
                data-testid={`my-task-row-${task.id}-shot-btn`}
                className="h-9 px-3 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                title={`Working scene file · ${accepts.accept}`}
              >
                {busy && uploadMode === 'shot' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : ok && uploadMode === 'shot' ? <Check className="w-3.5 h-3.5" />
                  : <FileBox className="w-3.5 h-3.5" />}
                {busy && uploadMode === 'shot' ? 'Uploading…' : ok && uploadMode === 'shot' ? 'Sent' : 'Upload Shot'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Inline error — surface the strict-naming rejection so artist learns */}
      {err && (
        <div
          className="mt-3 border border-rose-500/40 bg-rose-500/5 rounded p-3 text-[11px]"
          data-testid={`my-task-row-${task.id}-error`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-rose-200 font-semibold mb-1">{err.message}</p>
              {err.kind === 'dag_blocked' && err.missing_labels && (
                <p className="text-zinc-300 text-[10px]">
                  Wait for: <code className="font-mono text-amber-300">{err.missing_labels.join(', ')}</code> approval, then try again.
                </p>
              )}
              {err.kind === 'video_spec_mismatch' && (
                <div className="text-zinc-300 text-[10px] space-y-0.5 mt-1">
                  {(err.issues || []).map((it, i) => (
                    <p key={i} className="flex items-center gap-1.5">
                      <span className="text-rose-400">•</span> {it}
                    </p>
                  ))}
                </div>
              )}
              {err.expected_example && (
                <p className="text-zinc-300 text-[10px] mt-1">
                  Expected: <code className="font-mono text-emerald-300">{err.expected_example}</code>
                </p>
              )}
              {err.filename && (
                <p className="text-zinc-500 text-[10px] mt-0.5 truncate" title={err.filename}>
                  You uploaded: <code className="font-mono">{err.filename}</code>
                </p>
              )}
              {/* Help tip — founder ask May 24, 2026: every denial carries
                  a guidance line the artist can act on without asking PM. */}
              {err.help_tip && (
                <div
                  className="mt-2 flex items-start gap-1.5 p-2 rounded-sm bg-amber-500/10 border border-amber-500/30"
                  data-testid={`my-task-row-${task.id}-help-tip`}
                >
                  <Lightbulb className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-amber-200 text-[10px] leading-snug">
                    <span className="font-semibold uppercase tracking-wider mr-1">Tip:</span>
                    {err.help_tip}
                  </p>
                </div>
              )}
            </div>
            <button onClick={() => setErr(null)} className="text-zinc-500 hover:text-zinc-300 text-[10px] shrink-0">
              dismiss
            </button>
          </div>
        </div>
      )}

      {ok && (
        <div className="mt-2 text-[11px] text-emerald-300 flex items-center gap-1.5" data-testid={`my-task-row-${task.id}-success`}>
          <Check className="w-3.5 h-3.5" /> Uploaded successfully — moving to Producer Gate
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── MyTasksList — wrapper ───────────────────────────
//
// Groups tasks by department (using task.task_type) and renders a header
// per group. Reused by every dashboard so the visual is consistent.
//
// Pass `tasks` array of task objects (from any of the existing endpoints).
// Optional `emptyText` for the empty state.

export function MyTasksList({ tasks, onSubmitted, emptyText = 'No tasks yet — your producer hasn\'t allotted any work to you.', dense }) {
  if (!tasks || tasks.length === 0) {
    return (
      <div className="py-12 text-center" data-testid="my-tasks-empty">
        <FileVideo className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
        <p className="text-sm text-zinc-400">{emptyText}</p>
      </div>
    );
  }
  // Group by task_type for visual headers
  const groups = new Map();
  tasks.forEach(t => {
    const k = (t.task_type || t.stage || 'other').toLowerCase();
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(t);
  });
  return (
    <div className="space-y-5" data-testid="my-tasks-list">
      {Array.from(groups.entries()).map(([type, list]) => (
        <div key={type}>
          <div className="flex items-center gap-2 mb-2">
            <ChevronRight className="w-3 h-3 text-zinc-600" />
            <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
              {type.replace(/_/g, ' ')}
            </h3>
            <span className="text-[10px] text-zinc-600 font-mono">({list.length})</span>
          </div>
          <div className="space-y-2">
            {list.map(t => (
              <MyTasksRow key={t.id} task={t} onSubmitted={onSubmitted} dense={dense} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
